/** Analytics consent state + GA4 configuration.
 *
 *  The visitor's choice is stored in localStorage (not a cookie) so a declined
 *  visitor never receives any non-essential storage. The measurement ID comes
 *  from a build-time env var; an empty/unset ID disables analytics entirely
 *  (a safe no-op for local dev and any env without GA configured). */

export type ConsentChoice = 'granted' | 'denied'

const CONSENT_KEY = 'trc_analytics_consent'

/** GA4 measurement ID (public, client-side). Empty/unset → analytics off. */
export function getMeasurementId(): string | undefined {
  const raw = import.meta.env.VITE_GA4_MEASUREMENT_ID
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  return trimmed || undefined
}

export function readConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null
  try {
    const value = window.localStorage.getItem(CONSENT_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    return null
  }
}

export function writeConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(CONSENT_KEY, choice)
  } catch {
    /* storage blocked (private mode / disabled) — non-fatal */
  }
}

export function clearConsent(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(CONSENT_KEY)
  } catch {
    /* non-fatal */
  }
}

/** Event the footer dispatches to re-open the banner so a visitor can change
 *  a previous choice. The ConsentBanner listens for it. */
export const CONSENT_REOPEN_EVENT = 'trc:consent-reopen'
